import { useState, useEffect, useCallback, useRef } from 'react';
import { useWebSocket } from '../../../contexts/WebSocketContext';
import { TelemetrySample } from '@cbm/shared';

interface TelemetrySubscriptionOptions {
  maxSamples?: number;
  timeWindow?: number; // minutes
  tagFilter?: string[];
  onNewSample?: (sample: TelemetrySample) => void;
  onError?: (error: Error) => void;
}

interface TelemetryState {
  samples: TelemetrySample[];
  isLoading: boolean;
  error: string | null;
  lastUpdate: string | null;
  subscribedMachines: Set<string>;
}

export function useTelemetrySubscription(options: TelemetrySubscriptionOptions = {}) {
  const {
    maxSamples = 1000,
    timeWindow,
    tagFilter,
    onNewSample,
    onError,
  } = options;

  const { subscribeToMachine, unsubscribeFromMachine, lastMessage, isConnected } = useWebSocket();
  const [state, setState] = useState<TelemetryState>({
    samples: [],
    isLoading: false,
    error: null,
    lastUpdate: null,
    subscribedMachines: new Set(),
  });

  const stateRef = useRef(state);
  stateRef.current = state;

  // Process incoming telemetry messages
  useEffect(() => {
    if (!lastMessage || !isConnected) return;

    console.log('useTelemetrySubscription: Received message', { type: lastMessage.type });

    if (lastMessage.type === 'telemetry_update') {
      try {
        const { sample } = lastMessage.payload;
        
        console.log('useTelemetrySubscription: Processing telemetry', { 
          machineId: sample.machineId, 
          timestamp: sample.timestamp,
          tagCount: sample.tags.length 
        });
        
        // Apply tag filter if specified
        let filteredSample = sample;
        if (tagFilter && tagFilter.length > 0) {
          filteredSample = {
            ...sample,
            tags: sample.tags.filter((tag: any) => tagFilter.includes(tag.name)),
          };
        }

        // Apply time window filter if specified
        const now = new Date();
        const sampleTime = new Date(sample.timestamp);
        if (timeWindow && (now.getTime() - sampleTime.getTime()) > timeWindow * 60 * 1000) {
          return; // Skip old samples
        }

        setState(current => {
          const newSamples = [filteredSample, ...current.samples];
          
          // Apply time window filter to existing samples
          let filteredSamples = newSamples;
          if (timeWindow) {
            const cutoffTime = now.getTime() - timeWindow * 60 * 1000;
            filteredSamples = newSamples.filter(s => 
              new Date(s.timestamp).getTime() >= cutoffTime
            );
          }

          // Limit number of samples
          const limitedSamples = filteredSamples.slice(0, maxSamples);

          console.log('useTelemetrySubscription: Updated samples', { 
            totalSamples: limitedSamples.length,
            machineId: sample.machineId 
          });

          return {
            ...current,
            samples: limitedSamples,
            lastUpdate: sample.timestamp,
            error: null,
          };
        });

        // Call callback if provided
        onNewSample?.(filteredSample);

      } catch (error) {
        const errorMessage = error instanceof Error ? error.message : 'Unknown error processing telemetry';
        setState(current => ({
          ...current,
          error: errorMessage,
        }));
        onError?.(error instanceof Error ? error : new Error(errorMessage));
      }
    }
  }, [lastMessage, isConnected, tagFilter, timeWindow, maxSamples, onNewSample, onError]);

  // Subscribe to machine telemetry
  const subscribe = useCallback((machineId: string) => {
    if (!stateRef.current.subscribedMachines.has(machineId)) {
      setState(current => ({
        ...current,
        subscribedMachines: new Set([...current.subscribedMachines, machineId]),
        isLoading: true,
      }));
      
      subscribeToMachine(machineId);
    }
  }, [subscribeToMachine]);

  // Unsubscribe from machine telemetry
  const unsubscribe = useCallback((machineId: string) => {
    if (stateRef.current.subscribedMachines.has(machineId)) {
      setState(current => {
        const newSubscribed = new Set(current.subscribedMachines);
        newSubscribed.delete(machineId);
        
        return {
          ...current,
          subscribedMachines: newSubscribed,
          isLoading: newSubscribed.size > 0 && current.isLoading,
        };
      });
      
      unsubscribeFromMachine(machineId);
    }
  }, [unsubscribeFromMachine]);

  // Unsubscribe from all machines
  const unsubscribeAll = useCallback(() => {
    stateRef.current.subscribedMachines.forEach(machineId => {
      unsubscribeFromMachine(machineId);
    });
    
    setState(current => ({
      ...current,
      subscribedMachines: new Set(),
      isLoading: false,
    }));
  }, [unsubscribeFromMachine]);

  // Clear all samples
  const clearSamples = useCallback(() => {
    setState(current => ({
      ...current,
      samples: [],
      lastUpdate: null,
      error: null,
    }));
  }, []);

  // Get samples for specific machine
  const getSamplesForMachine = useCallback((machineId: string) => {
    return state.samples.filter(sample => sample.machineId === machineId);
  }, [state.samples]);

  // Get latest sample for machine
  const getLatestSampleForMachine = useCallback((machineId: string) => {
    const samples = getSamplesForMachine(machineId);
    return samples.length > 0 ? samples[0] : null;
  }, [getSamplesForMachine]);

  // Get tag values for specific machine and tag
  const getTagValues = useCallback((machineId: string, tagName: string) => {
    const samples = getSamplesForMachine(machineId);
    return samples
      .map(sample => {
        const tag = sample.tags.find(t => t.name === tagName);
        return tag ? { timestamp: sample.timestamp, value: tag.value, unit: tag.unit } : null;
      })
      .filter(Boolean) as Array<{ timestamp: string; value: number; unit: string }>;
  }, [getSamplesForMachine]);

  // Get all unique tag names
  const getAvailableTags = useCallback((machineId?: string) => {
    const samples = machineId ? getSamplesForMachine(machineId) : state.samples;
    const tagNames = new Set<string>();
    
    samples.forEach(sample => {
      sample.tags.forEach(tag => {
        tagNames.add(tag.name);
      });
    });
    
    return Array.from(tagNames);
  }, [state.samples, getSamplesForMachine]);

  // Get statistics for a tag
  const getTagStatistics = useCallback((machineId: string, tagName: string) => {
    const tagValues = getTagValues(machineId, tagName);
    
    if (tagValues.length === 0) {
      return null;
    }

    const values = tagValues.map(tv => tv.value);
    const sum = values.reduce((acc, val) => acc + val, 0);
    const mean = sum / values.length;
    const sortedValues = [...values].sort((a, b) => a - b);
    const median = sortedValues.length % 2 === 0
      ? (sortedValues[sortedValues.length / 2 - 1] + sortedValues[sortedValues.length / 2]) / 2
      : sortedValues[Math.floor(sortedValues.length / 2)];
    
    const variance = values.reduce((acc, val) => acc + Math.pow(val - mean, 2), 0) / values.length;
    const stdDev = Math.sqrt(variance);

    return {
      count: values.length,
      min: Math.min(...values),
      max: Math.max(...values),
      mean,
      median,
      stdDev,
      variance,
      latest: values[0],
      unit: tagValues[0].unit,
    };
  }, [getTagValues]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      unsubscribeAll();
    };
  }, [unsubscribeAll]);

  // Update loading state based on connection
  useEffect(() => {
    if (!isConnected && state.subscribedMachines.size > 0) {
      setState(current => ({
        ...current,
        isLoading: false,
        error: 'Connection lost',
      }));
    } else if (isConnected && state.subscribedMachines.size > 0 && state.samples.length === 0) {
      setState(current => ({
        ...current,
        isLoading: true,
        error: null,
      }));
    } else if (state.samples.length > 0) {
      setState(current => ({
        ...current,
        isLoading: false,
      }));
    }
  }, [isConnected, state.subscribedMachines.size, state.samples.length]);

  return {
    // State
    samples: state.samples,
    isLoading: state.isLoading,
    error: state.error,
    lastUpdate: state.lastUpdate,
    subscribedMachines: Array.from(state.subscribedMachines),
    isConnected,

    // Actions
    subscribe,
    unsubscribe,
    unsubscribeAll,
    clearSamples,

    // Data access
    getSamplesForMachine,
    getLatestSampleForMachine,
    getTagValues,
    getAvailableTags,
    getTagStatistics,
  };
}

// Specialized hook for single machine telemetry
export function useMachineTelemetry(
  machineId: string,
  options: Omit<TelemetrySubscriptionOptions, 'onNewSample'> & {
    onNewSample?: (sample: TelemetrySample, machineId: string) => void;
    autoSubscribe?: boolean;
  } = {}
) {
  const { onNewSample, autoSubscribe = true, ...telemetryOptions } = options;

  const subscription = useTelemetrySubscription({
    ...telemetryOptions,
    onNewSample: onNewSample ? (sample) => {
      if (sample.machineId === machineId) {
        onNewSample(sample, machineId);
      }
    } : undefined,
  });

  // Auto-subscribe to the machine
  useEffect(() => {
    if (autoSubscribe && machineId) {
      subscription.subscribe(machineId);
      return () => subscription.unsubscribe(machineId);
    }
  }, [machineId, autoSubscribe, subscription]);

  // Get machine-specific data
  const machineSamples = subscription.getSamplesForMachine(machineId);
  const latestSample = subscription.getLatestSampleForMachine(machineId);
  const availableTags = subscription.getAvailableTags(machineId);

  return {
    // Machine-specific state
    machineId,
    samples: machineSamples,
    latestSample,
    availableTags,
    isLoading: subscription.isLoading,
    error: subscription.error,
    lastUpdate: subscription.lastUpdate,
    isConnected: subscription.isConnected,
    isSubscribed: subscription.subscribedMachines.includes(machineId),

    // Actions
    subscribe: () => subscription.subscribe(machineId),
    unsubscribe: () => subscription.unsubscribe(machineId),
    clearSamples: subscription.clearSamples,

    // Data access helpers
    getTagValues: (tagName: string) => subscription.getTagValues(machineId, tagName),
    getTagStatistics: (tagName: string) => subscription.getTagStatistics(machineId, tagName),
  };
}